import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, Image, FolderOpen, Mail, BarChart3, Plus, Trash2, Edit2, Check, X, Eye } from 'lucide-react';

const AdminDashboard = () => {
  const { user, logout, authFetch } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [messages, setMessages] = useState([]);
  const [filterCat, setFilterCat] = useState('');
  const [showAddImage, setShowAddImage] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [newImage, setNewImage] = useState({ category_id: '', title: '', image_url: '', orientation: 'landscape' });

  const load = useCallback(async () => {
    const [s, c] = await Promise.all([
      authFetch('/api/admin/stats').then(r => r.json()),
      authFetch('/api/portfolio/categories').then(r => r.json()),
    ]);
    setStats(s);
    setCategories(c);
  }, [authFetch]);

  const loadImages = useCallback(async () => {
    const url = filterCat ? `/api/portfolio/images?category=${filterCat}` : '/api/portfolio/images';
    const imgs = await authFetch(url).then(r => r.json());
    setImages(imgs);
  }, [authFetch, filterCat]);

  const loadMessages = useCallback(async () => {
    const msgs = await authFetch('/api/admin/messages').then(r => r.json());
    setMessages(msgs);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'images') loadImages(); }, [tab, loadImages]);
  useEffect(() => { if (tab === 'messages') loadMessages(); }, [tab, loadMessages]);

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const addImage = async () => {
    if (!newImage.category_id || !newImage.title || !newImage.image_url) return;
    await authFetch('/api/admin/images', { method: 'POST', body: JSON.stringify(newImage) });
    setNewImage({ category_id: '', title: '', image_url: '', orientation: 'landscape' });
    setShowAddImage(false);
    loadImages();
    load();
  };

  const deleteImage = async (id) => {
    await authFetch(`/api/admin/images/${id}`, { method: 'DELETE' });
    loadImages();
    load();
  };

  const saveEdit = async () => {
    if (!editingImage) return;
    await authFetch(`/api/admin/images/${editingImage.id}`, {
      method: 'PUT',
      body: JSON.stringify({ title: editingImage.title, category_id: editingImage.category_id, orientation: editingImage.orientation })
    });
    setEditingImage(null);
    loadImages();
  };

  const markRead = async (id) => {
    await authFetch(`/api/admin/messages/${id}/read`, { method: 'PUT' });
    loadMessages();
    load();
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'images', label: 'Images', icon: Image },
    { id: 'categories', label: 'Categories', icon: FolderOpen },
    { id: 'messages', label: 'Messages', icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <a href="/" className="font-serif text-xl font-light tracking-tight" data-testid="admin-brand">NS</a>
          <span className="text-xs uppercase tracking-[0.2em] text-white/40">Admin Panel</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-white/40">{user?.email}</span>
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="text-white/40 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="flex">
        <aside className="w-48 md:w-56 border-r border-white/10 min-h-[calc(100vh-57px)] p-4">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-xs uppercase tracking-[0.15em] font-medium rounded transition-colors mb-1 ${
                tab === t.id ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </aside>

        <main className="flex-1 p-6 md:p-8 max-h-[calc(100vh-57px)] overflow-y-auto">
          {tab === 'overview' && stats && (
            <div data-testid="admin-overview">
              <h2 className="font-serif text-3xl font-light mb-8">Dashboard</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Images', value: stats.total_images, icon: Image },
                  { label: 'Categories', value: stats.total_categories, icon: FolderOpen },
                  { label: 'Messages', value: stats.total_messages, icon: Mail },
                  { label: 'Unread', value: stats.unread_messages, icon: Eye },
                ].map(s => (
                  <div key={s.label} className="bg-[#121212] border border-white/5 p-5" data-testid={`stat-${s.label.toLowerCase()}`}>
                    <s.icon className="w-5 h-5 text-white/30 mb-3" />
                    <p className="font-serif text-3xl font-light">{s.value}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'images' && (
            <div data-testid="admin-images">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-3xl font-light">Portfolio Images</h2>
                <button
                  onClick={() => setShowAddImage(!showAddImage)}
                  data-testid="admin-add-image-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black text-xs uppercase tracking-[0.15em] font-medium hover:bg-white/90 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Image
                </button>
              </div>

              <div className="flex gap-3 mb-6 flex-wrap">
                <button
                  onClick={() => setFilterCat('')}
                  className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${!filterCat ? 'border-white text-white' : 'border-white/10 text-white/40 hover:text-white/70'}`}
                >
                  All
                </button>
                {categories.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setFilterCat(c.id)}
                    data-testid={`admin-filter-${c.id}`}
                    className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${filterCat === c.id ? 'border-white text-white' : 'border-white/10 text-white/40 hover:text-white/70'}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>

              {showAddImage && (
                <div className="bg-[#121212] border border-white/10 p-5 mb-6 space-y-4" data-testid="admin-add-image-form">
                  <select
                    value={newImage.category_id}
                    onChange={e => setNewImage({ ...newImage, category_id: e.target.value })}
                    data-testid="admin-add-image-category"
                    className="w-full bg-[#1A1A1A] border border-white/10 text-white px-3 py-2 text-sm outline-none"
                  >
                    <option value="">Select Category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <input
                    placeholder="Image Title"
                    value={newImage.title}
                    onChange={e => setNewImage({ ...newImage, title: e.target.value })}
                    data-testid="admin-add-image-title"
                    className="w-full bg-transparent border-b border-white/15 py-2 text-sm text-white placeholder:text-white/25 outline-none"
                  />
                  <input
                    placeholder="Image URL"
                    value={newImage.image_url}
                    onChange={e => setNewImage({ ...newImage, image_url: e.target.value })}
                    data-testid="admin-add-image-url"
                    className="w-full bg-transparent border-b border-white/15 py-2 text-sm text-white placeholder:text-white/25 outline-none"
                  />
                  <select
                    value={newImage.orientation}
                    onChange={e => setNewImage({ ...newImage, orientation: e.target.value })}
                    data-testid="admin-add-image-orientation"
                    className="w-full bg-[#1A1A1A] border border-white/10 text-white px-3 py-2 text-sm outline-none"
                  >
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                  </select>
                  <div className="flex gap-3">
                    <button onClick={addImage} data-testid="admin-add-image-save" className="px-4 py-2 bg-white text-black text-xs uppercase tracking-[0.15em] font-medium">Save</button>
                    <button onClick={() => setShowAddImage(false)} className="px-4 py-2 border border-white/10 text-white/40 text-xs uppercase tracking-[0.15em]">Cancel</button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {images.map(img => (
                  <div key={img.id} className="group relative bg-[#121212] border border-white/5 overflow-hidden" data-testid={`admin-image-${img.id}`}>
                    {editingImage?.id === img.id ? (
                      <div className="p-3 space-y-2">
                        <input value={editingImage.title} onChange={e => setEditingImage({ ...editingImage, title: e.target.value })} className="w-full bg-transparent border-b border-white/15 py-1 text-xs text-white outline-none" />
                        <select value={editingImage.category_id} onChange={e => setEditingImage({ ...editingImage, category_id: e.target.value })} className="w-full bg-[#1A1A1A] border border-white/10 text-white px-2 py-1 text-xs outline-none">
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <div className="flex gap-2">
                          <button onClick={saveEdit} className="text-green-400"><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setEditingImage(null)} className="text-red-400"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <img src={img.image_url} alt={img.title} className="w-full aspect-square object-cover" loading="lazy" />
                        <div className="p-2">
                          <p className="text-xs text-white/60 truncate">{img.title}</p>
                          <p className="text-[10px] text-white/30 uppercase tracking-wider">{img.category_name}</p>
                        </div>
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => setEditingImage(img)} className="p-1.5 bg-black/70 text-white/60 hover:text-white" data-testid={`edit-${img.id}`}><Edit2 className="w-3 h-3" /></button>
                          <button onClick={() => deleteImage(img.id)} className="p-1.5 bg-black/70 text-red-400/60 hover:text-red-400" data-testid={`delete-${img.id}`}><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'categories' && (
            <div data-testid="admin-categories">
              <h2 className="font-serif text-3xl font-light mb-8">Categories</h2>
              <div className="space-y-3">
                {categories.map(c => (
                  <div key={c.id} className="bg-[#121212] border border-white/5 p-4 flex items-center justify-between" data-testid={`admin-category-${c.id}`}>
                    <div>
                      <p className="text-sm font-medium text-white">{c.name}</p>
                      <p className="text-xs text-white/30 uppercase tracking-wider">ID: {c.id} | Order: {c.sort_order}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'messages' && (
            <div data-testid="admin-messages">
              <h2 className="font-serif text-3xl font-light mb-8">Contact Messages</h2>
              <div className="space-y-3">
                {messages.map(msg => (
                  <div key={msg.id} className={`bg-[#121212] border p-5 ${msg.read ? 'border-white/5' : 'border-white/20'}`} data-testid={`admin-message-${msg.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-white">{msg.name}</p>
                        <p className="text-xs text-white/40">{msg.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-xs text-white/30">{new Date(msg.created_at).toLocaleDateString()}</p>
                        {!msg.read && (
                          <button onClick={() => markRead(msg.id)} data-testid={`mark-read-${msg.id}`} className="text-xs px-2 py-1 border border-white/10 text-white/40 hover:text-white transition-colors">
                            Mark Read
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-white/50 font-medium uppercase tracking-wider mb-1">{msg.subject}</p>
                    <p className="text-sm text-white/60 font-light">{msg.message}</p>
                  </div>
                ))}
                {messages.length === 0 && <p className="text-sm text-white/30">No messages yet.</p>}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
